import { Paper, type PaperProps } from '@mui/material';
import { forwardRef } from 'react';

interface GlassPanelProps extends PaperProps {
  /** Adds the border/shadow hover transition used on clickable cards. */
  hover?: boolean;
}

/**
 * Translucent, blurred surface used for hero/section/agent cards — the
 * building block for "the system should feel the same everywhere". Styling
 * lives in the `.glass-panel` / `.glass-panel-hover` CSS classes
 * (src/styles/global.css); the sx reset below just cancels Paper's own
 * border/shadow defaults so they don't fight the class.
 *
 * `flexShrink: 0` is load-bearing, not decorative: every page in this app
 * stacks GlassPanels inside a `flexDirection: 'column'` container, and
 * `.glass-panel`'s `overflow: hidden` (needed to clip the ambient glow blur)
 * strips flex items of their implicit `min-height: auto`. Without the
 * override here, a panel taller than its share of the flex container's
 * height gets silently shrunk below its own content height and the excess
 * is clipped — present in the DOM, invisible on screen. Callers can still
 * opt back into shrinking via `sx={{ flexShrink: 1 }}` if a given panel
 * genuinely needs to.
 */
export const GlassPanel = forwardRef<HTMLDivElement, GlassPanelProps>(function GlassPanel(
  { hover, className, sx, children, ...rest },
  ref,
) {
  return (
    <Paper
      ref={ref}
      elevation={0}
      className={['glass-panel', hover ? 'glass-panel-hover' : '', className].filter(Boolean).join(' ')}
      sx={{
        border: 'none',
        backgroundColor: 'transparent',
        boxShadow: 'none',
        borderRadius: 'var(--radius-xl)',
        flexShrink: 0,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Paper>
  );
});
